import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import { normRole, can } from "@/lib/permissions";

import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import DashboardPage from "@/pages/DashboardPage";
import OrdersListPage from "@/pages/OrdersListPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import OrderFormPage from "@/pages/OrderFormPage";
import ContactsListPage from "@/pages/ContactsListPage";
import ContactDetailPage from "@/pages/ContactDetailPage";
import ContactFormPage from "@/pages/ContactFormPage";
import DealsPage from "@/pages/DealsPage";
import SettingsPage from "@/pages/SettingsPage";
import ProductsListPage from "@/pages/ProductsListPage";
import ProductFormPage from "@/pages/ProductFormPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import { CollectionsListPage, CollectionDetailPage } from "@/pages/CollectionsPage";
import GoldRatesPage from "@/pages/GoldRatesPage";
import { StorefrontHomePage, StorefrontProductPage, StorefrontCollectionPage, StorefrontProductsPage, StorefrontCollectionsPage, StorefrontAboutPage, StorefrontContactPage } from "@/pages/StorefrontPages";
import { ProductionBoardPage, MyQueuePage, ProductionJobDetailPage } from "@/pages/ProductionPages";
import PublicTrackerPage from "@/pages/PublicTrackerPage";
import { CADQuotesPage, CADFilesPage } from "@/pages/CADPages";
import InvoicesPage from "@/pages/InvoicesPage";
import RoleMatrixPage from "@/pages/RoleMatrixPage";
import PresentationPage from "@/pages/PresentationPage";
import ProductBulkAddPage from "@/pages/ProductBulkAddPage";
import PublicCatalogPage from "@/pages/PublicCatalogPage";
import CatalogSharesPage from "@/pages/CatalogSharesPage";
import { ScanCardPage, MyVisitsPage, VisitDetailPage, MyExpensesPage, TeamJourneyPage } from "@/pages/FieldSalesPages";
import { RoutePlannerPage, RouteDetailPage, FollowUpsPage, PerformancePage } from "@/pages/FieldSalesV2Pages";
import ImportHubPage from "@/pages/ImportHubPage";
import { AssetsListPage, AssetDetailPage } from "@/pages/AssetsPages";
import { VisitorsPage } from "@/pages/VisitorsPage";
import { ProcurementPage } from "@/pages/ProcurementPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import MasterContactsPage from "@/pages/MasterContactsPage";
import PromotionRulesPage from "@/pages/PromotionRulesPage";
import CADDesignerPage from "@/pages/CADDesignerPage";
import EnchargePage from "@/pages/EnchargePage";
import WorkCostingPage from "@/pages/WorkCostingPage";
import DesignSearchPage from "@/pages/DesignSearchPage";
import AttendancePage from "@/pages/AttendancePage";
import VoiceCampaignsPage from "@/pages/VoiceCampaignsPage";
import FieldExtrasPage from "@/pages/FieldExtrasPage";
import HRPayrollPage from "@/pages/HRPayrollPage";
import AuditHubPage from "@/pages/AuditHubPage";
import CashFlowPage from "@/pages/CashFlowPage";
import PlanPage from "@/pages/PlanPage";
import { AppShell } from "@/components/AppShell";
import { PWAExperience } from "@/components/PWAExperience";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";

function ProtectedRoute({ children, capability }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-[#73716D]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (capability && !can(user, capability)) {
    return <AppShell><div className="text-center py-20"><div className="font-serif text-3xl">Not authorised</div><div className="text-[#4A5A50] mt-2">Your role does not have access to this page.</div></div></AppShell>;
  }
  return <AppShell>{children}</AppShell>;
}

function RoleHome() {
  const { user } = useAuth();
  const role = normRole(user?.role);
  if (role === "super-admin") return <Navigate to="/super-admin" replace/>;
  if (role === "production") return <Navigate to="/production/my-queue" replace/>;
  if (role === "client") return <Navigate to="/orders" replace/>;
  if (role === "accounts") return <Navigate to="/invoices" replace/>;
  if (role === "sales") return <Navigate to="/deals" replace/>;
  return <Navigate to="/dashboard" replace/>;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <Toaster position="top-right" richColors closeButton />
            <PWAExperience/>
            <ScrollToTop/>
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage/>}/>
            <Route path="/signup" element={<SignupPage/>}/>
            <Route path="/store/:companyCode" element={<StorefrontHomePage/>}/>
            <Route path="/store/:companyCode/products" element={<StorefrontProductsPage/>}/>
            <Route path="/store/:companyCode/products/:productId" element={<StorefrontProductPage/>}/>
            <Route path="/store/:companyCode/collections" element={<StorefrontCollectionsPage/>}/>
            <Route path="/store/:companyCode/collections/:collectionId" element={<StorefrontCollectionPage/>}/>
            <Route path="/store/:companyCode/about" element={<StorefrontAboutPage/>}/>
            <Route path="/store/:companyCode/contact" element={<StorefrontContactPage/>}/>
            <Route path="/track/:token" element={<PublicTrackerPage/>}/>
            <Route path="/c/:token" element={<PublicCatalogPage/>}/>

            {/* Protected app routes */}
            <Route path="/" element={<ProtectedRoute><RoleHome/></ProtectedRoute>}/>
            <Route path="/dashboard" element={<ProtectedRoute capability="dashboard.view"><DashboardPage/></ProtectedRoute>}/>

            <Route path="/orders" element={<ProtectedRoute capability="orders.view"><OrdersListPage/></ProtectedRoute>}/>
            <Route path="/orders/new" element={<ProtectedRoute capability="orders.create"><OrderFormPage/></ProtectedRoute>}/>
            <Route path="/orders/:id" element={<ProtectedRoute capability="orders.view"><OrderDetailPage/></ProtectedRoute>}/>
            <Route path="/orders/:id/edit" element={<ProtectedRoute capability="orders.edit"><OrderFormPage/></ProtectedRoute>}/>

            <Route path="/karigar" element={<ProtectedRoute><Navigate to="/production/my-queue" replace/></ProtectedRoute>}/>
            <Route path="/production/board" element={<ProtectedRoute capability="production.board"><ProductionBoardPage/></ProtectedRoute>}/>
            <Route path="/production/my-queue" element={<ProtectedRoute capability="production.my_queue"><MyQueuePage/></ProtectedRoute>}/>
            <Route path="/production/jobs/:id" element={<ProtectedRoute><ProductionJobDetailPage/></ProtectedRoute>}/>

            <Route path="/contacts" element={<ProtectedRoute capability="contacts.view"><ContactsListPage/></ProtectedRoute>}/>
            <Route path="/contacts/new" element={<ProtectedRoute capability="contacts.edit"><ContactFormPage/></ProtectedRoute>}/>
            <Route path="/contacts/:id" element={<ProtectedRoute capability="contacts.view"><ContactDetailPage/></ProtectedRoute>}/>

            <Route path="/deals" element={<ProtectedRoute capability="deals.view"><DealsPage/></ProtectedRoute>}/>

            <Route path="/products" element={<ProtectedRoute capability="catalog.view"><ProductsListPage/></ProtectedRoute>}/>
            <Route path="/products/new" element={<ProtectedRoute capability="catalog.edit"><ProductFormPage/></ProtectedRoute>}/>
            <Route path="/products/bulk-add" element={<ProtectedRoute capability="catalog.bulk_import"><ProductBulkAddPage/></ProtectedRoute>}/>
            <Route path="/products/present" element={<ProtectedRoute capability="catalog.presentation"><PresentationPage/></ProtectedRoute>}/>
            <Route path="/products/:id" element={<ProtectedRoute capability="catalog.view"><ProductDetailPage/></ProtectedRoute>}/>
            <Route path="/products/:id/edit" element={<ProtectedRoute capability="catalog.edit"><ProductFormPage/></ProtectedRoute>}/>

            <Route path="/collections" element={<ProtectedRoute capability="collections.view"><CollectionsListPage/></ProtectedRoute>}/>
            <Route path="/collections/:id" element={<ProtectedRoute capability="collections.view"><CollectionDetailPage/></ProtectedRoute>}/>

            <Route path="/catalog-shares" element={<ProtectedRoute capability="catalog.shares.view"><CatalogSharesPage/></ProtectedRoute>}/>

            <Route path="/gold-rates" element={<ProtectedRoute capability="gold_rates.view"><GoldRatesPage/></ProtectedRoute>}/>

            <Route path="/cad/quotes" element={<ProtectedRoute capability="cad.quotes.view"><CADQuotesPage/></ProtectedRoute>}/>
            <Route path="/cad/files" element={<ProtectedRoute capability="cad.files.view"><CADFilesPage/></ProtectedRoute>}/>

            <Route path="/invoices" element={<ProtectedRoute capability="invoices.view"><InvoicesPage/></ProtectedRoute>}/>

            <Route path="/settings" element={<ProtectedRoute capability="settings.view"><SettingsPage/></ProtectedRoute>}/>
            <Route path="/settings/roles" element={<ProtectedRoute capability="settings.roles"><RoleMatrixPage/></ProtectedRoute>}/>

            {/* Field Sales */}
            <Route path="/field/scan" element={<ProtectedRoute capability="field.own"><ScanCardPage/></ProtectedRoute>}/>
            <Route path="/field/visits" element={<ProtectedRoute capability="field.own"><MyVisitsPage/></ProtectedRoute>}/>
            <Route path="/field/visits/:id" element={<ProtectedRoute capability="field.own"><VisitDetailPage/></ProtectedRoute>}/>
            <Route path="/field/expenses" element={<ProtectedRoute><MyExpensesPage/></ProtectedRoute>}/>
            <Route path="/field/team" element={<ProtectedRoute capability="field.team.view"><TeamJourneyPage/></ProtectedRoute>}/>
            <Route path="/field/routes" element={<ProtectedRoute capability="field.own"><RoutePlannerPage/></ProtectedRoute>}/>
            <Route path="/field/routes/:id" element={<ProtectedRoute capability="field.own"><RouteDetailPage/></ProtectedRoute>}/>
            <Route path="/field/follow-ups" element={<ProtectedRoute capability="field.own"><FollowUpsPage/></ProtectedRoute>}/>
            <Route path="/field/performance" element={<ProtectedRoute capability="field.team.view"><PerformancePage/></ProtectedRoute>}/>
            <Route path="/field/extras" element={<ProtectedRoute><FieldExtrasPage/></ProtectedRoute>}/>
            <Route path="/hr" element={<ProtectedRoute><HRPayrollPage/></ProtectedRoute>}/>
            <Route path="/audit-hub" element={<ProtectedRoute><AuditHubPage/></ProtectedRoute>}/>
            <Route path="/cash-flow" element={<ProtectedRoute><CashFlowPage/></ProtectedRoute>}/>
            <Route path="/plan" element={<ProtectedRoute><PlanPage/></ProtectedRoute>}/>
            <Route path="/import" element={<ProtectedRoute><ImportHubPage/></ProtectedRoute>}/>

            {/* Factory Assets */}
            <Route path="/assets" element={<ProtectedRoute capability="assets.view"><AssetsListPage/></ProtectedRoute>}/>
            <Route path="/assets/:id" element={<ProtectedRoute capability="assets.view"><AssetDetailPage/></ProtectedRoute>}/>

            {/* Procurement (Watchman + Admin/Manager/Accounts) */}
            <Route path="/procurement" element={<ProtectedRoute capability="procurement.inward"><ProcurementPage/></ProtectedRoute>}/>

            {/* Visitors (Watchman + Admin/Manager and anyone as host) */}
            <Route path="/visitors" element={<ProtectedRoute><VisitorsPage/></ProtectedRoute>}/>

            {/* Super Admin (platform-level) */}
            <Route path="/super-admin" element={<ProtectedRoute capability="platform.super_admin"><SuperAdminPage/></ProtectedRoute>}/>
            <Route path="/super-admin/contacts" element={<ProtectedRoute capability="platform.super_admin"><MasterContactsPage/></ProtectedRoute>}/>
            <Route path="/super-admin/promotion-rules" element={<ProtectedRoute capability="platform.super_admin"><PromotionRulesPage/></ProtectedRoute>}/>

            {/* CAD Designer queue (cad_designer + coordinators) */}
            <Route path="/cad/designer" element={<ProtectedRoute capability="cad.designer.queue"><CADDesignerPage/></ProtectedRoute>}/>

            {/* Encharge (Email automation, admin+) */}
            <Route path="/settings/encharge" element={<ProtectedRoute capability="settings.view"><EnchargePage/></ProtectedRoute>}/>

            {/* Per-employee Work Costing */}
            <Route path="/work-costing" element={<ProtectedRoute><WorkCostingPage/></ProtectedRoute>}/>

            {/* Is this our design? (Visual Search) */}
            <Route path="/design-search" element={<ProtectedRoute><DesignSearchPage/></ProtectedRoute>}/>

            {/* Staff Attendance */}
            <Route path="/attendance" element={<ProtectedRoute><AttendancePage/></ProtectedRoute>}/>

            {/* AI Voice Campaigns (Super Admin only) */}
            <Route path="/super-admin/voice-campaigns" element={<ProtectedRoute capability="platform.super_admin"><VoiceCampaignsPage/></ProtectedRoute>}/>

            <Route path="*" element={<Navigate to="/" replace/>}/>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
